import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { AdminPermission } from "../../lib/acl/permissions";
import { toast } from "../../lib/toast";

export type UserGroup = {
  id: number;
  name: string;
  description: string | null;
  appAccess: string[];
};

export type AuthUser = {
  id: number;
  username: string;
  userLevel: number;
  email?: string | null;
  lastVisit?: number | null;
  role?: string | null; // Added role field to AuthUser
  avatarUrl?: string | null;
  bannerUrl?: string | null;
  isBanned?: boolean;
  permissions?: AdminPermission[]; // Admin permissions from assigned roles
  groups?: UserGroup[]; // User's group memberships
};

export type AuthSecurity = {
  twoFactorEnabled: boolean;
  passkeyCount: number;
};

export type AuthImpersonation = {
  active: boolean;
  adminUserId: number | null;
};

export type AppConfig = {
  instanceName?: string;
  defaultPage?: string;
  notifications: {
    externalChannelsDisabledForNonAdmin: boolean;
    emailConfigured: boolean;
    signalEnabled: boolean;
    discordBotEnabled: boolean;
    discordInviteLink: string | null;
    /**
     * Install-wide command-channel master enable, emitted by `meRoute`. Gates the
     * profile command sub-tab + sidebar entry; OFF by default. Optional for forward
     * compatibility with older payloads. The panel still self-gates via
     * `GET /api/me/command-channel/enabled` as defense-in-depth.
     */
    commandChannelEnabled?: boolean;
  };
  features: {
    registrationEnabled: boolean;
    webconsoleEnabled: boolean;
    wsdashboardEnabled: boolean;
    wsdMirrorEnabled?: boolean;
    configEditingEnabled: boolean;
    /**
     * Install-wide master enable for inter-instance data transfer. Gates the
     * Transfers nav section + `/transfers` surface; OFF by default. Optional for
     * forward compatibility until `meRoute` emits it.
     */
    interInstanceTransferEnabled?: boolean;
  };
  uploads: {
    avatarMaxSizeKb: number;
    bannerMaxSizeKb: number;
    avatarMaxBytes: number;
    bannerMaxBytes: number;
    allowAvatarUpload: boolean;
  };
  branding: {
    defaultAvatarUrl: string;
    defaultBannerUrl: string | null;
  };
  maintenance: {
    enabled: boolean;
    message: string | null;
    enabledAt: string | null;
    enabledByUserId: number | null;
    epoch: number;
  };
};

type AuthContextValue = {
  user: AuthUser | null;
  security: AuthSecurity | null;
  appConfig: AppConfig | null;
  impersonation: AuthImpersonation | null;
  csrfToken: string | null;
  loading: boolean;
  statusCode: number | null;
  refresh: (options?: { background?: boolean }) => Promise<void>;
  clear: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const HEARTBEAT_INTERVAL_MS = 30_000;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [security, setSecurity] = useState<AuthSecurity | null>(null);
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [impersonation, setImpersonation] = useState<AuthImpersonation | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusCode, setStatusCode] = useState<number | null>(null);
  const prevBannedRef = useRef<boolean | undefined>(undefined);

  const refresh = useCallback(async (options?: { background?: boolean; signal?: AbortSignal }) => {
    const isBackground = options?.background === true;

    if (!isBackground) {
      setLoading(true);
    }
    try {
      const res = await fetch("/api/me", { signal: options?.signal });
      if (options?.signal?.aborted) return;
      setStatusCode(res.status);
      if (!res.ok) {
        // Background polls: only clear auth state on definitive 401 (session
        // expired). Other HTTP errors (502/503 during restart, 500, etc.)
        // are transient — preserve existing auth state so the user isn't
        // kicked to /login while the backend restarts (e.g., during update).
        if (!isBackground || res.status === 401) {
          setUser(null);
          setSecurity(null);
          setAppConfig(null);
          setImpersonation(null);
          setCsrfToken(null);
        }
      } else {
        const data = await res.json();
        const newUser: AuthUser | null = data.user ?? null;
        setUser(newUser);
        setSecurity(data.security ?? null);
        setAppConfig(data.appConfig ?? null);
        setImpersonation(data.impersonation ?? null);
        setCsrfToken(data.csrfToken ?? null);

        // Detect ban transition: was not banned → now banned
        if (newUser?.isBanned && prevBannedRef.current === false) {
          toast.error("Your account has been suspended. Contact your administrator.", {
            duration: 15_000,
          });
        }
        prevBannedRef.current = newUser?.isBanned;
      }
    } catch (err) {
      if (options?.signal?.aborted) return;
      // Network errors (server unreachable) during background heartbeat:
      // preserve existing auth state. The server may be restarting (e.g.,
      // during a dashboard update). The next successful heartbeat will
      // restore fresh state. Only clear on foreground (initial load) failure.
      if (!isBackground) {
        setStatusCode(null);
        setUser(null);
        setSecurity(null);
        setAppConfig(null);
        setImpersonation(null);
        setCsrfToken(null);
      }
    } finally {
      if (!options?.signal?.aborted && !isBackground) {
        setLoading(false);
      }
    }
  }, []);

  const clear = useCallback(() => {
    setUser(null);
    setSecurity(null);
    setAppConfig(null);
    setImpersonation(null);
    setCsrfToken(null);
  }, []);

  // Initial fetch
  useEffect(() => {
    const controller = new AbortController();
    refresh({ signal: controller.signal });
    return () => controller.abort();
  }, []);

  // Heartbeat: poll /api/me every 30s when tab is visible and user is logged in
  useEffect(() => {
    // Only start heartbeat after initial load
    if (loading) return;
    // Only heartbeat when we have a user
    if (!user) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const tick = () => {
      if (document.hidden) return;
      void refresh({ background: true });
    };

    const handleVisibility = () => {
      if (!document.hidden) {
        // Resume immediately on tab focus
        void refresh({ background: true });
      }
    };

    timer = setInterval(tick, HEARTBEAT_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [loading, user !== null, refresh]);

  const value = useMemo(
    () => ({ user, security, appConfig, impersonation, csrfToken, loading, statusCode, refresh, clear }),
    [user, security, appConfig, impersonation, csrfToken, loading, statusCode, refresh, clear]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
};
